import axios from 'axios';
import { store } from '../redux/store';
import { InitPracticeResponse, AppointmentType, AppointmentTypeItem } from '../types';
import { clearAuth, updateToken } from '../redux/slices/authSlice';

const rawBaseUrl = process.env.REACT_APP_API_ENDPOINT
const normalizedBaseUrl = rawBaseUrl ? rawBaseUrl.replace(/\/+$/, '') : '';

const api = axios.create({
  baseURL: normalizedBaseUrl,
});

api.interceptors.request.use((config) => {
  const state: any = store.getState();
  const token: string | null = state?.auth?.token ?? null;
  if (token) {
    config.headers = config.headers || {};
    (config.headers as any)['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401) {
      if (error.response?.data?.errors?.includes("Invalid Credentials")) {
        store.dispatch(clearAuth());
        sessionStorage.clear();
        window.location.href = '/unauthorized';
        return Promise.reject(error);
      }
    }

    // Don't retry the refresh token endpoint itself
    if (originalRequest.url?.includes('/auth/refresh_token')) {
      store.dispatch(clearAuth());
      sessionStorage.clear();
      window.location.href = '/unauthorized';
      return Promise.reject(error);
    }
  
    if (error.response?.status === 401 && error.response.data.errors.includes("Token Expired") && !originalRequest._retry) {
      originalRequest._retry = true;

      const state: any = store.getState();
      const refreshToken = state?.auth?.refreshToken;

      if (!refreshToken) {
        store.dispatch(clearAuth());
        sessionStorage.clear();
        window.location.href = '/unauthorized';
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(`${normalizedBaseUrl}/auth/refresh_token`, {
          refresh_token: refreshToken,
        });

        const attrs = data?.data?.attributes;
        const newToken = attrs?.token;

        // Validate token exists
        if (!newToken) {
          throw new Error('No token in refresh response');
        }

        store.dispatch(updateToken({ token: newToken }));

        api.defaults.headers.common['Authorization'] = 'Bearer ' + newToken;
        originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
        originalRequest._retry = false;

        return api(originalRequest);
      } catch (err: any) {
        if (err.response?.data?.errors?.includes("Token Expired") && originalRequest._retry === true) {
          store.dispatch(clearAuth());
          sessionStorage.clear();
          window.location.href = '/session-expired';
          return Promise.reject(error);
        }
        else {
          store.dispatch(clearAuth());
          sessionStorage.clear();
          window.location.href = '/unauthorized';
          return Promise.reject(err);
        }       
      }
    }

    return Promise.reject(error);
  }
);

export async function initializePractice(practiceId: string): Promise<InitPracticeResponse> {
  const { data } = await api.post<any>('auth/token', {
    encrypted_practice_id: practiceId,
  });
  const attrs = (data && data.data && data.data.attributes) ? data.data.attributes : {};
  // Map appointment types
  const appointmentTypesRaw: AppointmentTypeItem[] = attrs?.appointment_types?.data || [];
  const appointment_types: AppointmentType[] = appointmentTypesRaw.map((item) => {
    const a = item.attributes;
    const provider = a?.provider?.data;
    return {
      id: item.id,
      title: a?.title,
      slug: a?.slug,
      treatmentCodes: a?.treatment_codes || [],
      newPatientMinutes: a?.new_patient_minutes,
      existingPatientMinutes: a?.existing_patient_minutes,
      bufferMinutes: a?.buffer_minutes,
      allowedPatientPosting: a?.allowed_patient_posting || 'both',
      provider: {
        id: provider?.id || '',
        firstName: provider?.attributes?.first_name || '',
        lastName: provider?.attributes?.last_name || '',
        role: provider?.attributes?.role || '',
        pmsProviderId: provider?.attributes?.pms_provider_id || '',
      },
    };
  });

  if (attrs.has_scheduler_access === false) {
    store.dispatch(clearAuth());
    sessionStorage.clear();
    window.location.href = '/unauthorized';
    return Promise.reject(new Error('Practice does not have scheduler access'));
  }

  const mapped: InitPracticeResponse = {
    token: attrs.token,
    refresh_token: attrs.refresh_token,
    practice: {
      id: data.id,
      title: attrs.title,
      address: attrs.address,
      email: attrs.email,
      logo: attrs.logo,
      phone_number: attrs.phone_number,
      promote_primetime: attrs.promote_primetime,
      website: attrs.website,
      working_hours: attrs.business_hours_weekly_summary,
      appointment_types: appointment_types,
      tenant_id: attrs.tenant_id,
      collect_insurance_details: attrs.collect_insurance_details ?? false,
      insurance_setting: attrs.insurance_setting?.data?.attributes ?? null,
      scheduler_type: attrs.scheduler_type,
      has_scheduler_access: attrs.has_scheduler_access,
      time_zone: attrs.time_zone,
      show_welcome_message: attrs.show_welcome_message,
      welcome_message: attrs.welcome_message,
    },
  };
  return mapped;
}

export interface AvailableSlotsParams {
  start_date: string;
  end_date: string;
  new_patient: boolean;
  fixed_duration_slots?: boolean;
  appointment_type_id?: string;
  duration_minutes?: number;
  filters?: string;
}

export interface AvailableSlotsResponseItem {
  id: string;
  type: 'operatory';
  attributes: {
    name: string;
    data_provider_id: string;
    available_slots: {
      [date: string]: Array<{
        start_time: string; // "07:00 UTC (+00:00)"
        end_time: string;
      }>;
    };
  };
}

export type AvailableSlotsResponse = AvailableSlotsResponseItem[];

export async function fetchAvailableSlots(params: AvailableSlotsParams): Promise<AvailableSlotsResponse> {
  try {
    const { data } = await api.get('available_slots', {
      params: {
        ...params,
        fixed_duration_slots: params.fixed_duration_slots ?? true,
      },
    });
    return data;
  } catch (error: any) {
    return [];
  }
}

export interface CreateAppointmentPayload {
  treatment_codes: string[];
  patient_type: 'new' | 'existing';
  duration: number;
  patient_request_id: string;
  reason: string | null;
  appointment_at: string
  operatoy_id: string;
  provider_id?: string;
  appointment_type_id: string;
  tenant_id: string;
}

export interface CreatePatientPayload {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  birth_date?: string;
  patient_type?: string;
  patient_id?: string | null;
  external_resource_id?: string;
  notes?: string;
  insurance_carrier_name?: string;
  insurance_member_id?: string;
  policy_holder_name?: string;
  policy_holder_dob?: string;
}
export interface RequestResponse {
  data: {
    id: string;
    type: 'request';
    attributes: {
      requestable_id: string;
      requestable_type: string;
    };
  };
}

export async function createAppointment(payload: CreateAppointmentPayload): Promise<RequestResponse> {
  try {
    const state: any = store.getState();
    const leadId = state?.auth?.leadId;

    const { data } = await api.post('requests', {
      request: {
        requestable_attributes: { ...payload },
        requestable_type: 'AppointmentRequest',
        action_type: 0,
        scheduling_lead_id: leadId,
      }
    });
    return data;
  } catch (error: any) {
    return error;
  }
}

export async function createPatient(payload: CreatePatientPayload): Promise<RequestResponse> {
  try {
    const state: any = store.getState();
    const leadId = state?.auth?.leadId;

    const { data } = await api.post('requests', {
      request: {
        requestable_attributes: { ...payload },
        requestable_type: 'PatientRequest',
        action_type: 0,
        scheduling_lead_id: leadId,
      }
    });
    return data;
  } catch (error: any) {
    return error;
  }
}

export async function updatePatient(requestId: string, payload: CreatePatientPayload): Promise<RequestResponse> {
  try {
    const state: any = store.getState();
    const leadId = state?.auth?.leadId;

    const { data } = await api.put(`requests/${requestId}`, {
      request: {
        requestable_attributes: { ...payload },
        requestable_type: 'PatientRequest',
        action_type: 0,
        scheduling_lead_id: leadId,
      }
    });
    return data;
  } catch (error: any) {
    return error;
  }
}

export default api;

export interface PatientSearchResponse {
  data: {
    id: string;
    type: 'patient' | 'patient_request';
    attributes: {
      first_name?: string;
      last_name?: string;
      email?: string;
      mobile_phone?: string;
      phone_number?: string;
      external_resource_id?: string;
      birth_date?: string;
      request_id?: string;
    };
  };
}

export interface PatientSearchPayload {
  patient_encrypted_id: string;
}

export interface ExistingPatientSearchPayload {
  first_name?: string;
  last_name?: string;
  email?: string;
  birth_date: string;
  phone_number: string;
}


export async function searchPatientDetails(payload: PatientSearchPayload): Promise<PatientSearchResponse> {
  const { data } = await api.post('searches/smart_search', {
    ...payload
  });
  return data;
}

export async function search_by_phone_birthdate(payload: ExistingPatientSearchPayload): Promise<PatientSearchResponse> {
  const { data } = await api.post('searches/patient_search', {
    search: { ...payload }
  });
  return data;
}

export interface CreateLeadPayload {
  lead_type: string;
  source_type: string;
  attribution_params: Record<string, string>;
}

export interface CreateLeadResponse {
  data: {
    id: string;
    type: 'scheduling_lead';
    attributes: {
      lead_type: string;
      source_type: string;
      attribution_params: Record<string, string>;
    };
  };
}

export async function createLead(payload: CreateLeadPayload): Promise<CreateLeadResponse> {
  const { data } = await api.post('scheduling_leads', {
    scheduling_lead: {
      lead_type: payload.lead_type,
      source_type: payload.source_type,
      attribution_params: payload.attribution_params,
    }
  });
  return data;
}
